import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { 
  MapPin, Navigation, Phone, AlertCircle, CheckCircle, 
  XCircle, Clock, User, RefreshCw, Image as ImageIcon, UserPlus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import L from 'leaflet';
import { AssignProviderDialog } from './AssignProviderDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom map icons
const customerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const providerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export const RoadsideManagement = () => {
  const [requests, setRequests] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTargetRequest, setAssignTargetRequest] = useState(null);

  useEffect(() => {
    fetchData();
    
    if (autoRefresh) {
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const fetchData = async () => {
    try {
      // Fetch all roadside requests
      const requestsRes = await axios.get(`${BACKEND_URL}/api/roadside/admin/all-requests`);
      setRequests(requestsRes.data.requests || []);

      // Fetch all providers
      const providersRes = await axios.get(`${BACKEND_URL}/api/roadside/admin/all-providers`);
      setProviders(providersRes.data.providers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const openAssignDialog = (request) => {
    setAssignTargetRequest(request);
    setAssignDialogOpen(true);
  };

  const handleAssigned = async () => {
    await fetchData();
  };

  const handleStatusOverride = async (requestNumber, newStatus) => {
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/roadside/admin/override-status`, {
        request_number: requestNumber,
        status: newStatus
      });
      toast.success(`Status updated to ${newStatus}`);
      await fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelRequest = async (requestNumber) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/roadside/admin/cancel-request`, {
        request_number: requestNumber
      });
      toast.success('Request cancelled');
      await fetchData();
      setSelectedRequest(null);
    } catch (error) {
      toast.error('Failed to cancel request');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Searching': 'bg-blue-100 text-blue-800 border-blue-300',
      'Assigned': 'bg-green-100 text-green-800 border-green-300',
      'En Route': 'bg-purple-100 text-purple-800 border-purple-300',
      'Arrived': 'bg-orange-100 text-orange-800 border-orange-300',
      'In Progress': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'Completed': 'bg-green-100 text-green-800 border-green-300',
      'Cancelled': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Get center point for map (average of all locations)
  const getMapCenter = () => {
    if (requests.length === 0) return [30.3322, -81.6557]; // Default: Jacksonville
    
    const lats = requests
      .filter(r => r.customer_location)
      .map(r => r.customer_location.latitude);
    const lons = requests
      .filter(r => r.customer_location)
      .map(r => r.customer_location.longitude);
    
    if (lats.length === 0) return [30.3322, -81.6557];
    
    const avgLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const avgLon = lons.reduce((a, b) => a + b, 0) / lons.length;
    
    return [avgLat, avgLon];
  };

  const activeRequests = requests.filter(r => 
    ['Searching', 'Assigned', 'En Route', 'Arrived', 'In Progress'].includes(r.status)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Roadside Assistance Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            {activeRequests.length} active request(s) • {requests.length} total
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setAutoRefresh(!autoRefresh)}
            variant="outline"
            size="sm"
            className={autoRefresh ? 'border-green-500' : ''}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Live Map */}
      <Card className="p-4">
        <h3 className="font-bold text-gray-900 mb-3">Live Map - All Active Requests</h3>
        <div className="h-96 rounded-lg overflow-hidden">
          <MapContainer
            center={getMapCenter()}
            zoom={11}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            
            {/* Customer markers */}
            {activeRequests.map((request) => (
              request.customer_location && (
                <Marker
                  key={request.request_number}
                  position={[request.customer_location.latitude, request.customer_location.longitude]}
                  icon={customerIcon}
                  eventHandlers={{
                    click: () => setSelectedRequest(request)
                  }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold">{request.service_type}</p>
                      <p>{request.vehicle_year} {request.vehicle_make} {request.vehicle_model}</p>
                      <p className="text-xs text-gray-600">{request.status}</p>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}

            {/* Provider markers */}
            {providers.map((provider) => (
              provider.current_location && provider.is_available && (
                <Marker
                  key={provider.provider_id}
                  position={[provider.current_location.latitude, provider.current_location.longitude]}
                  icon={providerIcon}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold">{provider.name}</p>
                      <p className="text-xs">{provider.is_available ? '🟢 Available' : '🔴 Busy'}</p>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>
      </Card>

      {/* Requests List */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">All Requests</h3>
        
        {requests.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No roadside requests yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <Card
                key={request.request_number}
                className={`p-4 cursor-pointer transition-all ${
                  selectedRequest?.request_number === request.request_number
                    ? 'border-2 border-blue-500 bg-blue-50'
                    : 'border hover:border-gray-300'
                }`}
                onClick={() => setSelectedRequest(request)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-bold text-gray-900">{request.service_type}</h4>
                    <p className="text-sm text-gray-600">#{request.request_number}</p>
                  </div>
                  <Badge className={`${getStatusColor(request.status)} border`}>
                    {request.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">Vehicle:</span>
                    <p className="font-medium">{request.vehicle_year} {request.vehicle_make} {request.vehicle_model}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Customer:</span>
                    <p className="font-medium">{request.customer_name}</p>
                  </div>
                </div>

                {request.provider_name && (
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600">Assigned to:</span>
                    <span className="font-medium ml-2">{request.provider_name}</span>
                  </div>
                )}

                {/* Quick Actions */}
                {selectedRequest?.request_number === request.request_number && (
                  <div className="mt-4 pt-4 border-t flex gap-2 flex-wrap items-center">
                    {!['Completed', 'Cancelled'].includes(request.status) && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssignDialog(request);
                        }}
                        disabled={isLoading}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid={`assign-btn-${request.request_number}`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        {request.provider_id ? 'Reassign' : 'Assign'}
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusOverride(request.request_number, 'Completed')}
                      disabled={isLoading || request.status === 'Completed'}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Mark Complete
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCancelRequest(request.request_number)}
                      disabled={isLoading || ['Completed', 'Cancelled'].includes(request.status)}
                      className="border-red-200 text-red-600"
                    >
                      <XCircle className="w-3 h-3 mr-1" />
                      Cancel
                    </Button>

                    {request.customer_photos && request.customer_photos.length > 0 && (
                      <Button size="sm" variant="outline">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        View Photos ({request.customer_photos.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </Card>

      <AssignProviderDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        request={assignTargetRequest}
        onAssigned={handleAssigned}
      />
    </div>
  );
};
